<template>
    <div class="ui-page">
        <ui-page-header title="Trilha do certame" subtitle="Tudo o que aconteceu no processo, em ordem. Cada registro leva o hash do anterior: qualquer alteração quebra a corrente.">
            <v-btn outlined :disabled="!processId" :loading="checking" @click="verify"><v-icon left small>mdi-link-variant</v-icon>Conferir corrente</v-btn>
        </ui-page-header>

        <div v-if="check" class="cp-notice" :class="{ 'cp-notice--danger': !check.ok }">
            <v-icon small>{{ check.ok ? "mdi-check-decagram-outline" : "mdi-link-variant-off" }}</v-icon>
            <span v-if="check.ok"><strong>Corrente íntegra.</strong> {{ check.total }} {{ check.total === 1 ? "registro conferido" : "registros conferidos" }}<span v-if="check.head">; último hash <span class="ui-mono">{{ check.head.slice(0, 16) }}…</span></span>.</span>
            <span v-else><strong>Corrente quebrada no registro {{ check.broken_at }}.</strong> Esse registro ou o anterior foi alterado ou apagado fora da plataforma.</span>
        </div>

        <div v-if="stamps" class="cp-notice" :class="{ 'cp-notice--danger': stamps.counts.failed }">
            <v-icon small>{{ stamps.counts.failed ? "mdi-clock-alert-outline" : "mdi-clock-check-outline" }}</v-icon>
            <div>
                <strong>Carimbo do tempo (ACT, RFC 3161).</strong>
                {{ stamps.counts.stamped }} {{ stamps.counts.stamped === 1 ? "evento carimbado" : "eventos carimbados" }}<span v-if="stamps.counts.pending">, {{ stamps.counts.pending }} na fila</span><span v-if="stamps.counts.failed">, {{ stamps.counts.failed }} com falha</span>.
                <span v-if="!stamps.configured">A ACT não está configurada (COMPETITIVE_TSA_URL): os eventos esperam na fila.</span>
                <div v-for="item in failedStamps" :key="item.id" class="mt-1">
                    #{{ item.sequence }} {{ eventLabel(item.event) }}: {{ item.last_error }}
                    <v-btn x-small outlined class="ml-2" :loading="retrying === item.id" @click="retry(item)">Pedir de novo</v-btn>
                </div>
            </div>
        </div>

        <ui-table-card>
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <v-select v-model="event" :items="eventItems" placeholder="Todos os eventos" clearable dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"></v-select>
            </template>
            <v-data-table :headers="headers" :items="rows" :loading="loading" disable-sort disable-pagination hide-default-footer
                :no-data-text="processId ? 'Nenhum registro' : 'Selecione um processo'" loading-text="Carregando...">
                <template v-slot:[`item.sequence`]="{ item }"><span class="ui-mono">#{{ item.sequence }}</span></template>
                <template v-slot:[`item.created_at`]="{ item }"><span class="ui-nowrap">{{ formatStamp(item.created_at, { seconds: true }) }}</span></template>
                <template v-slot:[`item.event`]="{ item }">
                    <div class="ui-cell-main">{{ eventLabel(item.event) }}</div>
                    <div v-if="describe(item)" class="ui-cell-sub">{{ describe(item) }}</div>
                </template>
                <template v-slot:[`item.actor`]="{ item }">
                    <div>{{ item.actor_name || ACTOR_LABELS[item.actor_type] }}</div>
                    <div class="ui-cell-sub">{{ ACTOR_LABELS[item.actor_type] }}<span v-if="item.ip"> · {{ item.ip }}</span></div>
                </template>
                <template v-slot:[`item.hash`]="{ item }"><span class="ui-mono ui-small" :title="item.hash">{{ item.hash.slice(0, 12) }}…</span></template>
                <template v-slot:[`item.timestamp`]="{ item }">
                    <template v-if="item.timestamp && item.timestamp.status === 'stamped'">
                        <a class="ui-nowrap ui-small" title="Baixar o carimbo (.tst) para conferir com openssl ts -verify" @click="downloadToken(item)">
                            <v-icon x-small color="success">mdi-clock-check-outline</v-icon> {{ formatStamp(item.timestamp.gen_time, { seconds: true }) }}
                        </a>
                    </template>
                    <span v-else-if="item.timestamp && item.timestamp.status === 'failed'" class="ui-small error--text">Falhou</span>
                    <span v-else-if="item.timestamp" class="ui-small ui-muted">Na fila</span>
                </template>
            </v-data-table>
            <template #footer>
                <div class="ui-pagination">
                    <span class="ui-small ui-muted">{{ total }} {{ total === 1 ? "registro" : "registros" }}</span>
                    <span class="ui-pagination__right">
                        <v-btn small outlined :disabled="page <= 1" @click="page -= 1">Anterior</v-btn>
                        <v-btn small outlined :disabled="page * 50 >= total" @click="page += 1">Próxima</v-btn>
                    </span>
                </div>
            </template>
        </ui-table-card>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatMoney, formatStamp, notifyError } from "@/components/competitive/competitive.util";

const EVENT_LABELS = {
    "registration.started": "Inscrição iniciada",
    "registration.document_uploaded": "Documento enviado pelo proponente",
    "registration.document_removed": "Documento removido pelo proponente",
    "registration.submitted": "Inscrição enviada para análise",
    "registration.withdrawn": "Desistência do proponente",
    "registration.resumed": "Inscrição retomada",
    "registration.document_reviewed": "Documento conferido",
    "registration.request_correction": "Correção pedida",
    "registration.approve": "Proponente habilitado",
    "registration.reject": "Proponente inabilitado",
    "registration.reopen": "Análise reaberta",
    "nda.published": "Termo de confidencialidade publicado",
    "nda.accepted": "Termo de confidencialidade aceito",
    "term.published": "Termo de inscrição atualizado",
    "term.signed": "Termo de inscrição assinado (ICP-Brasil)",
    "nda.signed": "Termo de confidencialidade assinado (ICP-Brasil)",
    "dataroom.document_added": "Documento incluído no data room",
    "dataroom.document_removed": "Documento retirado do data room",
    "dataroom.downloaded": "Documento do data room baixado",
    "custodian.invited": "Custódio convidado",
    "custodian.reinvited": "Convite do custódio reenviado",
    "custodian.removed": "Custódio removido",
    "custodian.login": "Acesso do custódio",
    "custodian.key_registered": "Chave pessoal do custódio registrada",
    "custodian.key_replaced": "Chave pessoal do custódio trocada",
    "urn.sealed": "Urna lacrada",
    "share.downloaded": "Fragmento baixado pelo custódio",
    "share.withdrawn": "Fragmento retirado e conferido",
    "envelope.received": "Proposta lacrada recebida",
    "opening.started": "Sessão de abertura iniciada",
    "opening.restarted": "Sessão de abertura reiniciada com outra chave",
    "share.submitted": "Fragmento entregue na sessão de abertura",
    "urn.opened": "Urna aberta; chave publicada",
    "urn.closed": "Urna fechada; código do conjunto publicado",
    "bid.placed": "Lance na rodada",
    "round.closed": "Rodada de lances encerrada",
    "final.convoked": "Convocação para o termo de proposta final",
    "final.signed": "Termo de proposta final assinado",
    "final.lapsed": "Termo de proposta final não assinado no prazo",
    "homologation.registered": "Decisão de homologação registrada",
    "minutes.generated": "Ata emitida",
    "report.generated": "Relatório técnico emitido",
};
const ACTOR_LABELS = { admin: "Equipe B&J", user: "Proponente", custodian: "Custódio", system: "Plataforma" };
const SIGNED_AS = { holder: "e-CPF do titular", company: "e-CNPJ da empresa", representative: "e-CPF do representante" };
const DOC_STATUS = { approved: "aprovado", rejected: "recusado", pending: "voltou para conferência" };

export default {
    name: "CompetitiveAuditTrail",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        ACTOR_LABELS,
        rows: [],
        total: 0,
        page: 1,
        event: null,
        loading: false,
        checking: false,
        check: null,
        stamps: null,
        retrying: null,
        eventItems: Object.entries(EVENT_LABELS).map(([value, text]) => ({ value, text })),
        headers: [
            { text: "Nº", value: "sequence", width: 70 },
            { text: "Quando", value: "created_at", width: 170 },
            { text: "Evento", value: "event" },
            { text: "Quem", value: "actor", width: 220 },
            { text: "Hash", value: "hash", width: 130 },
            { text: "Carimbo do tempo", value: "timestamp", width: 170 },
        ],
    }),
    watch: {
        event() {
            this.page = 1;
            this.load();
        },
        page() {
            this.load();
        },
    },
    computed: {
        failedStamps() {
            return this.stamps ? this.stamps.data.filter((item) => item.status === "failed") : [];
        },
    },
    methods: {
        formatStamp,
        eventLabel(event) {
            return EVENT_LABELS[event] || event;
        },
        // Detalhe curto a partir do payload gravado.
        describe(item) {
            const payload = item.payload || {};
            if (item.event === "envelope.received") return `Etapa ${payload.stage} · protocolo ${payload.protocol}${payload.replaces ? ` (substitui ${payload.replaces})` : ""}`;
            if (item.event === "urn.opened") return `Etapa ${payload.stage} · ${payload.envelopes} envelope(s) · ${payload.valid_offers} proposta(s) válida(s)`;
            if (item.event === "bid.placed") return `Etapa ${payload.stage} · ${formatMoney(payload.value)}${payload.extended ? " · prorrogou a rodada" : ""}`;
            if (item.event === "round.closed") return `Etapa ${payload.stage} · ${formatMoney(payload.winning_value)} · ${payload.by_bid ? `${payload.bids} lance(s)` : "sem lance, vale a proposta"}${payload.extensions ? ` · ${payload.extensions} prorrogação(ões)` : ""}`;
            if (item.event === "homologation.registered") return `Etapa ${payload.stage} · decisão de ${String(payload.decision_date || "").split("-").reverse().join("/")} · ${(payload.items || []).map((entry) => `${entry.code} ${entry.decision === "homologated" ? "homologado" : "não homologado"}`).join(", ")}`;
            if (item.event === "report.generated") return `${payload.kind === "stage" ? `Etapa ${payload.stage}` : `Inscrição ${payload.registration_id}`} · ${payload.public_id} · versão ${payload.version}`;
            if (item.event === "minutes.generated") return `Etapa ${payload.stage} · ${payload.partial ? "ata parcial" : "ata"} · SHA-256 ${String(payload.sha256 || "").slice(0, 16)}… · ${(payload.attachments || []).length} anexo(s)`;
            if (item.event === "final.convoked") return `Etapa ${payload.stage} · ${payload.position}º colocado · ${formatMoney(payload.value)} · prazo até ${formatStamp(payload.deadline_at)}`;
            if (item.event === "final.lapsed") return `Etapa ${payload.stage} · ${payload.position}º colocado não assinou até ${formatStamp(payload.deadline_at)}`;
            if (item.event === "final.signed") return `${formatMoney(payload.value)} · ${SIGNED_AS[payload.signed_as] || payload.signed_as} · ${payload.signer_cnpj ? `CNPJ ${payload.signer_cnpj}` : `CPF ${payload.signer_cpf}`}`;
            if (item.event === "share.submitted") return `Etapa ${payload.stage} · fragmento ${payload.share_index}`;
            if (item.event === "opening.started" || item.event === "opening.restarted") return `Etapa ${payload.stage} · sessão ${String(payload.session_fingerprint || "").slice(0, 16)}…`;
            if (item.event === "urn.closed") return `Etapa ${payload.stage} · ${payload.envelope_count} envelope(s) · conjunto ${String(payload.set_hash || "").slice(0, 16)}…`;
            if (payload.protocol) return `Protocolo ${payload.protocol}`;
            if (payload.signature_id) return `Versão ${payload.version} · ${SIGNED_AS[payload.signed_as] || payload.signed_as} · ${payload.signer_cnpj ? `CNPJ ${payload.signer_cnpj}` : `CPF ${payload.signer_cpf}`}`;
            if (item.event === "term.published") return payload.default ? `Versão ${payload.version} (texto padrão)` : `Versão ${payload.version}`;
            if (item.event === "registration.document_reviewed") return `Arquivo ${DOC_STATUS[payload.status] || payload.status}${payload.note ? `: ${payload.note}` : ""}`;
            if (payload.reason) return payload.reason;
            if (payload.version) return `Versão ${payload.version}`;
            if (payload.title) return payload.title;
            if (payload.file_hash) return `SHA-256 ${payload.file_hash.slice(0, 16)}…`;
            return "";
        },
        onProcess() {
            this.check = null;
            this.page = 1;
            this.load();
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/audit-logs`, { params: { page: this.page, event: this.event || undefined } });
                this.rows = result.data.content.data;
                this.total = result.data.content.total;
                this.stamps = (await axios.get(`${API}/processes/${this.processId}/timestamps`)).data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        async retry(item) {
            this.retrying = item.id;
            try {
                await axios.post(`${API}/timestamps/${item.id}/retry`);
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.retrying = null;
            }
        },
        async downloadToken(item) {
            try {
                const result = await axios.get(`${API}/timestamps/${item.timestamp.id}/token`, { responseType: "blob" });
                const url = URL.createObjectURL(result.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = `carimbo-${item.sequence}.tst`;
                link.click();
                URL.revokeObjectURL(url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async verify() {
            this.checking = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/audit-logs/verify`);
                this.check = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.checking = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
